// API service using Supabase (Production - Supabase Required)
import { supabase } from './supabase';

// Helper function to filter out blob URLs from image fields
const filterBlobUrls = (items) => {
  if (!Array.isArray(items)) return items;
  
  return items.map(item => {
    if (!item || typeof item !== 'object') return item;
    
    const filtered = { ...item };
    
    // Common image URL field names
    const imageFields = ['image_url', 'url', 'photo_url', 'photo', 'logo_url', 'logo', 'imageUrl', 'image'];
    
    imageFields.forEach(field => {
      if (filtered[field] && typeof filtered[field] === 'string' && filtered[field].startsWith('blob:')) {
        filtered[field] = null;
      }
    });
    
    return filtered;
  });
};

// Reviews API
export const getReviews = async () => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

export const saveReview = async (reviewData) => {
  try {
    const { id, photo, imageUrl, image, ...dataToSave } = reviewData;

    // Ensure only valid database columns are sent
    const cleanData = {
      name: dataToSave.name,
      email: dataToSave.email || null,
      rating: dataToSave.rating,
      text: dataToSave.text,
      course: dataToSave.course || null,
      photo_url: dataToSave.photo_url || photo || imageUrl || image || null,
      video_url: dataToSave.video_url || null,
      is_video_review: dataToSave.is_video_review || false,
      is_public: dataToSave.is_public !== undefined ? dataToSave.is_public : true,
    };

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('reviews')
        .update(cleanData)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('reviews')
        .insert([cleanData])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving review:', error);
    throw error;
  }
};

export const deleteReview = async (id) => {
  try {
    // First, get review to delete photo file
    const { data: review, error: fetchError } = await supabase
      .from('reviews')
      .select('photo_url')
      .eq('id', id)
      .single();

    if (!fetchError && review?.photo_url && review.photo_url.includes('photos/reviews/')) {
      const pathMatch = review.photo_url.match(/photos\/reviews\/(.+)$/);
      if (pathMatch) {
        await supabase.storage.from('photos').remove([`reviews/${pathMatch[1]}`]);
      }
    }

    const { error } = await supabase.from('reviews').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting review:', error);
    throw error;
  }
};

// Success Stories API
export const getSuccessStories = async () => {
  try {
    const { data, error } = await supabase
      .from('success_stories')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return filterBlobUrls(data || []);
  } catch (error) {
    console.error('Error fetching success stories:', error);
    throw error;
  }
};

export const saveSuccessStory = async (storyData) => {
  try {
    const { id, photo, imageUrl, image, ...dataToSave } = storyData;

    // Ensure only valid database columns are sent
    const cleanData = {
      name: dataToSave.name,
      course: dataToSave.course || null,
      university: dataToSave.university || null,
      story: dataToSave.story,
      category: dataToSave.category || 'general',
      photo_url: dataToSave.photo_url || photo || imageUrl || image || null,
      visa_document_url: dataToSave.visa_document_url || null,
    };

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('success_stories')
        .update(cleanData)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('success_stories')
        .insert([cleanData])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving success story:', error);
    throw error;
  }
};

export const deleteSuccessStory = async (id) => {
  try {
    // First, get story to delete photo file
    const { data: story, error: fetchError } = await supabase
      .from('success_stories')
      .select('photo_url, visa_document_url')
      .eq('id', id)
      .single();

    if (!fetchError && story) {
      // Delete photo
      if (story.photo_url && story.photo_url.includes('photos/stories/')) {
        const pathMatch = story.photo_url.match(/photos\/stories\/(.+)$/);
        if (pathMatch) {
          await supabase.storage.from('photos').remove([`stories/${pathMatch[1]}`]);
        }
      }
      
      // Delete visa document if exists
      if (story.visa_document_url && story.visa_document_url.includes('photos/')) {
        const visaPathMatch = story.visa_document_url.match(/photos\/(.+)$/);
        if (visaPathMatch) {
          await supabase.storage.from('photos').remove([visaPathMatch[1]]);
        }
      }
    }

    const { error } = await supabase.from('success_stories').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting success story:', error);
    throw error;
  }
};

// Photos API
export const getPhotos = async (category = 'all') => {
  try {
    let query = supabase
      .from('photos')
      .select('*')
      .order('created_at', { ascending: false });

    if (category !== 'all') {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return filterBlobUrls(data || []);
  } catch (error) {
    console.error('Error fetching photos:', error);
    throw error;
  }
};

export const uploadPhoto = async (photoData) => {
  try {
    const { file, category, title } = photoData;

    // Upload file to Supabase Storage
    let photoUrl = photoData.url;
    let storagePath = null;

    if (file) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `photos/${category}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      storagePath = filePath;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from('photos').getPublicUrl(filePath);
      photoUrl = publicUrl;
    }

    // Save photo metadata to database
    const { data, error } = await supabase
      .from('photos')
      .insert([
        {
          title: title || '',
          category: category || 'gallery',
          url: photoUrl,
          storage_path: storagePath,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
};

export const savePhoto = async (photoData) => {
  try {
    const { id, ...dataToSave } = photoData;

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('photos')
        .update(dataToSave)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('photos')
        .insert([dataToSave])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving photo:', error);
    throw error;
  }
};

export const deletePhoto = async (id) => {
  try {
    // First, get photo to delete file
    const { data: photo, error: fetchError } = await supabase
      .from('photos')
      .select('storage_path, url')
      .eq('id', id)
      .single();

    if (!fetchError && photo) {
      // Delete from storage if path exists
      if (photo.storage_path) {
        await supabase.storage.from('photos').remove([photo.storage_path]);
      } else if (photo.url && photo.url.includes('photos/')) {
        // Try to extract path from URL
        const pathMatch = photo.url.match(/photos\/(.+)$/);
        if (pathMatch) {
          await supabase.storage.from('photos').remove([pathMatch[1]]);
        }
      }
    }

    const { error } = await supabase.from('photos').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

// Universities API
export const getUniversities = async () => {
  try {
    const { data, error } = await supabase
      .from('universities')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return filterBlobUrls(data || []);
  } catch (error) {
    console.error('Error fetching universities:', error);
    throw error;
  }
};

export const saveUniversity = async (universityData) => {
  try {
    const { id, logo, ...dataToSave } = universityData;

    // Ensure only valid database columns are sent
    const cleanData = {
      name: dataToSave.name,
      country: dataToSave.country,
      domain: dataToSave.domain || null,
      rank: dataToSave.rank || null,
      logo_url: dataToSave.logo_url || logo || null,
    };

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('universities')
        .update(cleanData)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('universities')
        .insert([cleanData])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving university:', error);
    throw error;
  }
};

export const deleteUniversity = async (id) => {
  try {
    const { data: university, error: fetchError } = await supabase
      .from('universities')
      .select('logo_url')
      .eq('id', id)
      .single();

    if (!fetchError && university?.logo_url && university.logo_url.includes('photos/universities/')) {
      const pathMatch = university.logo_url.match(/photos\/universities\/(.+)$/);
      if (pathMatch) {
        await supabase.storage.from('photos').remove([`universities/${pathMatch[1]}`]);
      }
    }

    const { error } = await supabase.from('universities').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting university:', error);
    throw error;
  }
};

// Students API
export const getStudents = async (universityId = null) => {
  try {
    let query = supabase
      .from('students')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (universityId) {
      query = query.eq('university_id', universityId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return filterBlobUrls(data || []);
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
};

export const saveStudent = async (studentData) => {
  try {
    const { id, photo, imageUrl, image, ...dataToSave } = studentData;

    // Ensure only valid database columns are sent
    const cleanData = {
      name: dataToSave.name,
      email: dataToSave.email || null,
      phone: dataToSave.phone || null,
      course: dataToSave.course,
      university_id: dataToSave.university_id || null,
      university_name: dataToSave.university_name || null,
      country: dataToSave.country || null,
      visa_type: dataToSave.visa_type || null,
      visa_document_url: dataToSave.visa_document_url || null,
      photo_url: dataToSave.photo_url || photo || imageUrl || image || null,
      story: dataToSave.story || null,
      status: dataToSave.status || 'placed',
      intake_year: dataToSave.intake_year || null,
      intake_month: dataToSave.intake_month || null,
    };

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('students')
        .update(cleanData)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('students')
        .insert([cleanData])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving student:', error);
    throw error;
  }
};

export const deleteStudent = async (id) => {
  try {
    const { data: student, error: fetchError } = await supabase
      .from('students')
      .select('photo_url, visa_document_url')
      .eq('id', id)
      .single();

    if (!fetchError && student) {
      // Delete photo
      if (student.photo_url && student.photo_url.includes('photos/students/')) {
        const photoPathMatch = student.photo_url.match(/photos\/students\/(.+)$/);
        if (photoPathMatch) {
          await supabase.storage.from('photos').remove([`students/${photoPathMatch[1]}`]);
        }
      }
      
      // Delete visa document
      if (student.visa_document_url && student.visa_document_url.includes('photos/students/visa/')) {
        const visaPathMatch = student.visa_document_url.match(/photos\/students\/visa\/(.+)$/);
        if (visaPathMatch) {
          await supabase.storage.from('photos').remove([`students/visa/${visaPathMatch[1]}`]);
        }
      }
    }

    const { error } = await supabase.from('students').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting student:', error);
    throw error;
  }
};

// Carousel API
export const getCarouselSlides = async () => {
  try {
    const { data, error } = await supabase
      .from('carousel_slides')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return filterBlobUrls(data || []);
  } catch (error) {
    console.error('Error fetching carousel slides:', error);
    throw error;
  }
};

export const saveCarouselSlide = async (slideData) => {
  try {
    const { id, url, order, image, ...dataToSave } = slideData;

    // Ensure only valid database columns are sent
    const cleanData = {
      image_url: dataToSave.image_url || url || image || null,
      show_card: dataToSave.show_card || false,
      card_title: dataToSave.card_title || null,
      card_description: dataToSave.card_description || null,
      card_link: dataToSave.card_link || null,
      display_order: dataToSave.display_order !== undefined ? dataToSave.display_order : (order !== undefined ? order : 0),
    };

    let data, error;
    if (id) {
      ({ data, error } = await supabase
        .from('carousel_slides')
        .update(cleanData)
        .eq('id', id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('carousel_slides')
        .insert([cleanData])
        .select()
        .single());
    }

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving carousel slide:', error);
    throw error;
  }
};

export const deleteCarouselSlide = async (id) => {
  try {
    const { error } = await supabase.from('carousel_slides').delete().eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting carousel slide:', error);
    throw error;
  }
};

// Popup Settings API
export const getPopupSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('popup_settings')
      .select('*')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Supabase error:', error);
      throw error;
    }

    if (!data) {
      // Return default settings if no record exists (in milliseconds)
      return {
        min_interval: 30000, // 30 seconds
        max_interval: 90000, // 90 seconds
        enabled: true,
        enabled_templates: ['template1'],
      };
    }

    // Ensure intervals are in milliseconds (for backward compatibility)
    return {
      ...data,
      min_interval: data.min_interval || 30000,
      max_interval: data.max_interval || 90000,
      enabled: data.enabled !== undefined ? data.enabled : true,
    };
  } catch (error) {
    console.error('Error fetching popup settings:', error);
    // Return defaults on error
    return {
      min_interval: 30000,
      max_interval: 90000,
      enabled: true,
      enabled_templates: ['template1'],
    };
  }
};

// Carousel Settings API
export const getCarouselSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('carousel_settings')
      .select('*')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Supabase error:', error);
      throw error;
    }

    if (!data) {
      // Return default settings if no record exists
      return {
        transition_type: 'fade',
        transition_duration: 1200,
        auto_play_interval: 4500,
        auto_play_enabled: true,
        easing_function: 'ease-in-out',
        slide_direction: 'right',
        pause_on_hover: true,
        show_navigation: false,
        show_indicators: true,
        blur_intensity: 0,
        brightness_level: 100,
      };
    }

    return data;
  } catch (error) {
    console.error('Error fetching carousel settings:', error);
    // Return defaults on error
    return {
      transition_type: 'fade',
      transition_duration: 1200,
      auto_play_interval: 4500,
      auto_play_enabled: true,
      easing_function: 'ease-in-out',
      slide_direction: 'right',
      pause_on_hover: true,
      show_navigation: false,
      show_indicators: true,
      blur_intensity: 0,
      brightness_level: 100,
    };
  }
};

export const saveCarouselSettings = async (settingsData) => {
  try {
    const { data, error } = await supabase
      .from('carousel_settings')
      .upsert({ id: 1, ...settingsData, updated_at: new Date().toISOString() })
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving carousel settings:', error);
    throw error;
  }
};

export const savePopupSettings = async (settingsData) => {
  try {
    const { error } = await supabase
      .from('popup_settings')
      .upsert([{ id: 1, ...settingsData }], { onConflict: 'id' });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving popup settings:', error);
    throw error;
  }
};
